import { useState, useRef, useCallback, useEffect } from 'react';
import { CusProductListUsePriceSectionOptions, CusProductListUsePriceSectionResult } from '../cus-product-list.data';
import { PERCENT_MULTIPLIER, PRICE_RANGE_MIN_GAP } from '../cus-product-list.const';

const clamp = (value: number, lo: number, hi: number) => Math.min(Math.max(value, lo), hi);

const toPercent = (value: number, minPrice: number, range: number) => (range === 0 ? 0 : ((value - minPrice) / range) * PERCENT_MULTIPLIER);

export default function usePriceSection({
    minPrice,
    maxPrice,
    selectedRange,
    onChange
}: CusProductListUsePriceSectionOptions): CusProductListUsePriceSectionResult {
    const [minVal, setMinVal] = useState(selectedRange ? clamp(selectedRange.min, minPrice, maxPrice) : minPrice);
    const [maxVal, setMaxVal] = useState(selectedRange ? clamp(selectedRange.max, minPrice, maxPrice) : maxPrice);
    const [isCollapsed, setIsCollapsed] = useState(false);

    const trackRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef<'min' | 'max' | null>(null);

    const range = maxPrice - minPrice;

    useEffect(() => {
        if (selectedRange) {
            setMinVal(clamp(selectedRange.min, minPrice, maxPrice));
            setMaxVal(clamp(selectedRange.max, minPrice, maxPrice));
        } else {
            setMinVal(minPrice);
            setMaxVal(maxPrice);
        }
    }, [selectedRange, minPrice, maxPrice]);

    const getValueFromPosition = useCallback(
        (clientX: number): number => {
            const track = trackRef.current;
            if (!track) {
                return minPrice;
            }
            const rect = track.getBoundingClientRect();
            const percent = (clientX - rect.left) / rect.width;
            return Math.round(minPrice + percent * range);
        },
        [minPrice, range]
    );

    const handleMove = useCallback(
        (clientX: number) => {
            const raw = getValueFromPosition(clientX);
            if (draggingRef.current === 'min') {
                setMinVal(clamp(raw, minPrice, maxVal - PRICE_RANGE_MIN_GAP));
            } else if (draggingRef.current === 'max') {
                setMaxVal(clamp(raw, minVal + PRICE_RANGE_MIN_GAP, maxPrice));
            }
        },
        [getValueFromPosition, minPrice, maxVal, minVal, maxPrice]
    );

    const handleEnd = useCallback(() => {
        if (draggingRef.current) {
            draggingRef.current = null;
            onChange?.(minVal, maxVal);
        }
    }, [minVal, maxVal, onChange]);

    const toggleCollapsed = () => setIsCollapsed(prev => !prev);

    const startDrag = useCallback(
        (handle: 'min' | 'max') => (e: React.PointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            draggingRef.current = handle;
            e.currentTarget.setPointerCapture(e.pointerId);
            handleMove(e.clientX);
        },
        [handleMove]
    );

    const handleDragMove = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (!draggingRef.current) {
                return;
            }
            handleMove(e.clientX);
        },
        [handleMove]
    );

    const handleDragEnd = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
            handleEnd();
        },
        [handleEnd]
    );

    return {
        minVal,
        maxVal,
        minPercent: toPercent(minVal, minPrice, range),
        maxPercent: toPercent(maxVal, minPrice, range),
        isCollapsed,
        trackRef,
        toggleCollapsed,
        startDrag,
        handleDragMove,
        handleDragEnd
    };
}
